import React, { useState } from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Caption } from '../Shared/TypographyComponent';

/*
Code Block Component
--------------------
Beautiful syntax-highlighted code blocks with copy functionality
*/

function CodeBlock({ language = 'javascript', code, showLineNumbers = true }) {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Custom theme matching site palette with sky blue accents
  const customStyle = {
    'pre[class*="language-"]': {
      background: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa',
      border: `1px solid ${theme.palette.primary.tertiary}40`, // sky blue at 25% opacity
      borderRadius: '8px',
      margin: 0,
      padding: '1.5rem',
      fontSize: '0.9rem',
      lineHeight: 1.8,
      color: theme.palette.text.primary,
    },
    'code[class*="language-"]': {
      background: 'transparent',
      fontSize: '0.9rem',
      fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
      color: theme.palette.text.primary,
    },
    // Keywords (def, class, if, return, etc.)
    'keyword': {
      color: theme.palette.primary.tertiary, // sky blue for keywords
      fontWeight: 'bold',
    },
    // Function names
    'function': {
      color: theme.palette.primary.tertiary, // sky blue
    },
    // Class names
    'class-name': {
      color: theme.palette.primary.tertiary, // sky blue
      fontWeight: 'bold',
    },
    // Strings
    'string': {
      color: theme.palette.mode === 'dark' ? '#98C379' : '#50a14f', // green
    },
    // Comments
    'comment': {
      color: theme.palette.mode === 'dark' ? '#5c6370' : '#a0a1a7',
      fontStyle: 'italic',
    },
    // Numbers
    'number': {
      color: theme.palette.mode === 'dark' ? '#D19A66' : '#986801',
    },
    // Operators
    'operator': {
      color: theme.palette.text.primary,
    },
    // Punctuation
    'punctuation': {
      color: theme.palette.mode === 'dark' ? '#ABB2BF' : '#383a42',
    },
    // Properties and attributes
    'property': {
      color: theme.palette.primary.tertiary, // sky blue
    },
    'attr-name': {
      color: theme.palette.primary.tertiary, // sky blue
    },
    // Booleans and constants
    'boolean': {
      color: theme.palette.primary.tertiary,
    },
    'constant': {
      color: theme.palette.primary.tertiary,
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        my: 3,
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa',
        border: `1px solid ${theme.palette.primary.tertiary}40`, // sky blue at 25% opacity
      }}
    >
      {/* Header with language and copy button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: `${theme.palette.primary.tertiary}15`, // sky blue at 8% opacity
          borderBottom: `1px solid ${theme.palette.primary.tertiary}40`, // sky blue at 25% opacity
        }}
      >
        <Caption sx={{ opacity: 0.6, textTransform: 'uppercase' }}>
          {language}
        </Caption>

        <Tooltip title={copied ? 'Copied!' : 'Copy code'} placement="left">
          <IconButton
            onClick={handleCopy}
            size="small"
            sx={{
              color: copied ? theme.palette.text.link : `${theme.palette.text.primary}99`, // text primary at 60% opacity
              '&:hover': {
                color: theme.palette.text.link,
                backgroundColor: `${theme.palette.text.link}1A`, // link color at 10% opacity
              },
            }}
          >
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Code content */}
      <Box
        sx={{
          '& pre': {
            margin: '0 !important',
            padding: '1.5rem !important',
          },
          '& code': {
            fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
          },
          // Custom scrollbar
          '& pre::-webkit-scrollbar': {
            height: '8px',
            width: '8px',
          },
          '& pre::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#e5e5e5',
          },
          '& pre::-webkit-scrollbar-thumb': {
            backgroundColor: `${theme.palette.primary.tertiary}60`, // sky blue at 38% opacity
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme.palette.primary.tertiary, // full sky blue on hover
            },
          },
        }}
      >
        <SyntaxHighlighter
          language={language}
          style={customStyle}
          showLineNumbers={showLineNumbers}
          lineNumberStyle={{
            minWidth: '3em',
            paddingRight: '1em',
            color: `${theme.palette.primary.tertiary}60`, // sky blue at 38% opacity
            borderRight: `1px solid ${theme.palette.primary.tertiary}30`, // sky blue at 19% opacity
            marginRight: '1em',
            userSelect: 'none',
          }}
          customStyle={{
            background: 'transparent',
            margin: 0,
            padding: '1.5rem',
            fontSize: '0.9rem',
          }}
        >
          {code}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
}

export default CodeBlock;
